import { useMemo } from 'react';

import {
  GRID_TREE_DATA_GROUPING_FIELD,
  GridColDef,
  GridSortCellParams,
  GridSortDirection,
  GridValidRowModel,
  gridStringOrNumberComparator,
} from '@mui/x-data-grid-premium';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import { SortOption } from '../GeneratikTableToolbar.types';

export interface UseProcessedColumnsParams<R extends GridValidRowModel> {
  baseColumns: GridColDef<R>[];
  columns: GeneratikTableColumn<R>[];
  rows: R[];
  sortOptions?: SortOption[];
  groupByField?: string | null;
  treeDataEnabled: boolean;
  effectiveGetTreeDataPath: ((row: R) => Array<string | number>) | undefined;
}

export interface UseProcessedColumnsReturn<R extends GridValidRowModel> {
  processedColumns: GridColDef<R>[];
  autoHiddenColumns: Set<string>;
}

const useProcessedColumns = <R extends GridValidRowModel>({
  baseColumns,
  columns,
  rows,
  sortOptions,
  groupByField,
  treeDataEnabled,
  effectiveGetTreeDataPath,
}: UseProcessedColumnsParams<R>): UseProcessedColumnsReturn<R> => {
  const processedColumns = useMemo(() => {
    return baseColumns.map((col) => {
      const isSortable = !sortOptions?.length || sortOptions.some((o) => o.field === col.field);
      const isGroupedColumn = groupByField === col.field;

      if (isSortable && !isGroupedColumn && !groupByField) return col;

      const baseComparator = col.sortComparator ?? gridStringOrNumberComparator;

      // Sorting a column while grouped reorders rows only WITHIN each group; the
      // group buckets keep their natural (pre-sort) order. Synthetic group-by
      // buckets are auto-generated rows that sit alone at the top level, so
      // comparing them as equal leaves them in insertion order under MUI's stable
      // sort. Real rows that merely became tree parents (e.g. package bookings
      // with child spaces) are NOT auto-generated — they sit among their leaf
      // siblings and must still sort by their own cell value, so they fall
      // through to the base comparator (the comparator returning 0 for any group
      // node — including those package rows — was the original "in-group sorting
      // is frozen + intransitive around packages" bug). MUI prefers
      // `getSortComparator` over `sortComparator` and never negates it for `desc`
      // (see `parseSortItem` in @mui/x-data-grid), so direction handling lives here.
      const isSyntheticGroupNode = (node: GridSortCellParams['rowNode']): boolean =>
        node?.type === 'group' && node.isAutoGenerated;

      const getGroupAwareSortComparator =
        (direction: GridSortDirection) =>
        (v1: unknown, v2: unknown, p1: GridSortCellParams, p2: GridSortCellParams) => {
          if (isSyntheticGroupNode(p1?.rowNode) || isSyntheticGroupNode(p2?.rowNode)) return 0;
          const directionFactor = direction === 'desc' ? -1 : 1;
          return directionFactor * baseComparator(v1, v2, p1, p2);
        };

      return {
        ...col,
        ...(groupByField && { getSortComparator: getGroupAwareSortComparator }),
        ...(!isSortable && { sortable: false }),
        ...(isGroupedColumn && { hideable: false }),
      };
    });
  }, [baseColumns, sortOptions, groupByField]);

  const autoHiddenColumns = useMemo(() => {
    const hidden = new Set<string>();
    if (rows.length === 0) return hidden;

    for (const col of columns) {
      if (col.isRowEmpty && rows.every((row) => col.isRowEmpty!(row))) {
        hidden.add(col.field);
      }
    }

    if (treeDataEnabled && effectiveGetTreeDataPath && !groupByField) {
      const hasChildren = rows.some((row) => effectiveGetTreeDataPath(row).length > 1);
      if (!hasChildren) {
        hidden.add(GRID_TREE_DATA_GROUPING_FIELD);
      }
    }

    return hidden;
  }, [columns, rows, treeDataEnabled, effectiveGetTreeDataPath, groupByField]);

  return { processedColumns, autoHiddenColumns };
};

export default useProcessedColumns;
